import { isDevelopment } from "./development";
import type { EventProperties, PropValue } from "./types";

/**
 * Keys the types reject outright. Email and name belong on the user (via
 * `identify`), never on events — see docs/principles.md § Analytics & data privacy.
 */
export type ForbiddenKey = "email" | "name" | "first_name" | "last_name" | "phone" | "phone_number";

/** Added by sampling; an event may not supply it. */
export type ReservedKey = "sample_rate";

const RESERVED_KEY: ReservedKey = "sample_rate";

/**
 * The runtime rule, which also catches what the types cannot see (spreads of
 * wider objects, case and naming variants).
 *
 * A key is split into lower-cased segments on `_`, `-`, spaces and camelCase
 * boundaries (`contactFirstName` → `contact`, `first`, `name`). A one-letter
 * lower-case prefix stays attached (`iPhone` → `iphone`). It is personal data if:
 *
 * - its last segment is `email` or `phone` (`email`, `user_email`, `contactPhone`), or
 * - its last two segments are `email address` or `phone number` (`emailAddress`, `phone_number`), or
 * - it is exactly `name` or `username`, or
 * - its last segment is `name` and the one before is `first`, `last`, `full`,
 *   `user`, `display`, `given` or `contact` (`first_name`, `contact_name`).
 *
 * Except: a key whose first segment is `is` or `has` is a boolean flag about the
 * data, not the data, and is kept (`has_phone`, `is_email_verified`).
 *
 * So `email`/`phone` elsewhere in a key are kept (`email_sent`, `emailVerified`,
 * `phone_model`, `device_phone_type`), as are whole-word look-alikes (`phoneme`,
 * `emailed`) and organisational names (`camera_name`, `site_name`, `button_name`,
 * `name_filter`).
 */
function isPersonalDataKey(key: string): boolean {
  const segments = key
    .replace(/([a-z0-9]{2,})([A-Z])/g, "$1_$2")
    .toLowerCase()
    .split(/[_\-\s]+/)
    .filter((segment) => segment !== "");
  const [first] = segments;
  if (first === "is" || first === "has") {
    return false;
  }
  const last = segments.at(-1);
  const beforeLast = segments.at(-2);
  if (segments.length === 1 && (last === "name" || last === "username")) {
    return true;
  }
  if (last === "email" || last === "phone") {
    return true;
  }
  if ((beforeLast === "email" && last === "address") || (beforeLast === "phone" && last === "number")) {
    return true;
  }
  return last === "name" && PERSON_NAME_QUALIFIERS.has(beforeLast ?? "");
}

const PERSON_NAME_QUALIFIERS: ReadonlySet<string> = new Set([
  "first",
  "last",
  "full",
  "user",
  "display",
  "given",
  "contact",
]);

/** Objects, functions and arrays of anything but strings are not sent. */
function isPropValue(value: unknown): value is PropValue {
  switch (typeof value) {
    case "string":
    case "number":
    case "boolean":
    case "undefined":
      return true;
    case "object":
      return value === null || (Array.isArray(value) && value.every((item) => typeof item === "string"));
    default:
      return false;
  }
}

/**
 * Returns the props that may be sent: without personal data, without invalid
 * values, and without `undefined` (so it cannot erase a route context value).
 * Everything but `undefined` is reported in development.
 */
export function sanitise(eventName: string, props: Readonly<Record<string, unknown>>): EventProperties {
  const kept: Record<string, PropValue> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined) {
      continue;
    }
    if (key === RESERVED_KEY) {
      warnDropped(eventName, key, "it is reserved for sampling");
      continue;
    }
    if (isPersonalDataKey(key)) {
      warnDropped(eventName, key, "personal data is not allowed in events");
      continue;
    }
    if (!isPropValue(value)) {
      warnDropped(eventName, key, "values must be a string, number, boolean, null or string array");
      continue;
    }
    kept[key] = value;
  }
  return kept;
}

function warnDropped(eventName: string, key: string, reason: string): void {
  if (isDevelopment()) {
    console.warn(`[a3s-analytics] Dropped "${key}" from "${eventName}": ${reason}.`);
  }
}
